import React from "react";
import { JSONElement } from "@plugins/gatsby-source-paligo/src/types";
import {
    H2,
    H3,
    H4,
    H5,
    H6,
    HeadingAnchor,
} from "@singlestore/fusion/components/typography";
import { useSiteContext } from "contexts/SiteContext";

import { copyToClipboard } from "utils/copy-to-clipboard";
import { getAbsoluteUrl } from "utils/url";

type DynamicHeaderProps = {
    jsonElement: JSONElement;
    showAnchor?: boolean;
};

export function DynamicHeader({
    children,
    jsonElement,
    showAnchor = true,
}: React.PropsWithChildren<DynamicHeaderProps>) {
    const { pathname } = useSiteContext();

    const level = Number(jsonElement?.attributes?.headerLevel);
    const id = jsonElement?.attributes?.id;
    const absoluteUrl = getAbsoluteUrl({ path: pathname, id });

    let headingAnchor;
    if (showAnchor) {
        headingAnchor = (
            <HeadingAnchor
                aria-labelledby={id}
                onClick={() => copyToClipboard(absoluteUrl)}
            />
        );
    }

    switch (level) {
        case 1:
            return (
                <H2 id={id} variant="heading-5">
                    {headingAnchor}
                    {children}
                </H2>
            );

        case 2:
            return (
                <H3 id={id} variant="heading-4">
                    {headingAnchor}
                    {children}
                </H3>
            );

        case 3:
            return (
                <H4 id={id} variant="heading-3">
                    {headingAnchor}
                    {children}
                </H4>
            );

        case 4:
            return (
                <H5 id={id} variant="heading-2">
                    {headingAnchor}
                    {children}
                </H5>
            );

        case 5:
        case 6:
        default:
            return (
                <H6 id={id} variant="heading-1">
                    {headingAnchor}
                    {children}
                </H6>
            );
    }
}
